import api, { LoanActivity } from '@api';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import {
    Button, Divider, Skeleton, Typography
} from '@mui/material';
import { ProgressIndicator, useAsyncEffect, usePageMessage } from '@tsp-ui/core';
import { useGetCurrentAccount } from '@utils';
import { useCallback, useState } from 'react';

import { ActivityStreamItem } from './ActivityStreamItem';
import styles from './ActivityStreamSection.module.scss';


export function ActivityStreamSection() {
    const { id: clientId, customerId } = useGetCurrentAccount();
    const [ loanActivity, setLoanActivity ] = useState<LoanActivity[]>([]);
    const [ loanActivityPageNumber, setLoanActivityPageNumber ] = useState(1);
    const [ loanActivityLoading, setLoanActivityLoading ] = useState(false);
    const [ moreActivityLoading, setMoreActivityLoading ] = useState<'older' | 'newer' | null>(null);
    const [ totalPages, setTotalPages ] = useState(0);
    const pageMessage = usePageMessage();

    // fetch initial loan activity
    useAsyncEffect(useCallback(async () => {
        setLoanActivityLoading(true);

        try {
            const loanActivityResponse = await api.loanActivity.getLoanActivity(clientId, customerId);

            setLoanActivity(loanActivityResponse.data);
            setTotalPages(loanActivityResponse.totalPages);
        } catch (error) {
            pageMessage.error('An error occurred while fetching loan activity');
        }

        setLoanActivityLoading(false);
    }, [
        clientId, customerId, pageMessage
    ]));

    async function getNextPage(direction: 'older' | 'newer') {
        setMoreActivityLoading(direction);

        try {
            const loanActivityResult = await api.loanActivity.getLoanActivity(
                clientId, customerId, { pageNumber: direction === 'older' ? loanActivityPageNumber + 1 : loanActivityPageNumber - 1 }
            );

            setLoanActivity(loanActivityResult.data);
            setLoanActivityPageNumber(loanActivityResult.pageNumber);
        } catch (error) {
            pageMessage.error('An error occurred while fetching loan activity');
        }

        setMoreActivityLoading(null);
    }

    return (
        <div className={styles.sidebar}>
            <div>
                <Typography
                    fontWeight={500}
                    className={styles.header}
                >
                    Activity stream
                </Typography>

                <div className={styles.activityStream}>
                    {}

                    {loanActivityLoading ? <Skeleton height={34} /> : !loanActivity.length ? (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="center"
                        >
                            No recent loan activity found in this account
                        </Typography>
                    ) : (
                        <>
                            {loanActivityPageNumber > 1 && (
                                <Button
                                    size="small"
                                    onClick={() => getNextPage('newer')}
                                    disabled={!!moreActivityLoading}
                                >
                                    Load newer activity

                                    {moreActivityLoading === 'newer' ? (
                                        <ProgressIndicator
                                            size={16}
                                            className={styles.loadIcon}
                                            color="inherit"
                                        />
                                    ) : (
                                        <ArrowUpward
                                            fontSize="small"
                                            color="inherit"
                                            className={styles.loadIcon}
                                        />
                                    )}
                                </Button>
                            )}

                            {loanActivity?.map(loanActivityItem => (
                                <>
                                    <ActivityStreamItem loanActivity={loanActivityItem} />

                                    <Divider className={styles.divider} />
                                </>
                            ))}

                            {totalPages > loanActivityPageNumber ? (
                                <Button
                                    size="small"
                                    onClick={() => getNextPage('older')}
                                    disabled={!!moreActivityLoading}
                                >
                                    Load older activity

                                    {moreActivityLoading === 'older' ? (
                                        <ProgressIndicator
                                            size={17}
                                            className={styles.loadIcon}
                                            color="inherit"
                                        />
                                    ) : (
                                        <ArrowDownward
                                            fontSize="small"
                                            className={styles.loadIcon}
                                            color="inherit"
                                        />
                                    )}
                                </Button>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
